import React from "react";
import { alpha, Box, Button, Chip, Paper, Stack, Typography, useTheme } from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import VideoLibraryIcon from "@mui/icons-material/VideoLibrary";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";

interface Props {
  videoDataListLength: number;
  currentPage: number;
  viewedVideos: Set<number>;
  handlePageChange: (event: React.ChangeEvent<unknown>, page: number) => void;
  handlePrevVideo: () => void;
  handleNextVideo: () => void;
}

export const VideoNavigationControls: React.FC<Props> = ({
  videoDataListLength,
  currentPage,
  viewedVideos,
  handlePageChange,
  handlePrevVideo,
  handleNextVideo,
}) => {
  const theme = useTheme();
  const totalVideos = videoDataListLength;
  const viewedCount = viewedVideos.size;
  const allVideosViewed = totalVideos <= 1 || viewedCount >= totalVideos;

  return (
    <Paper
      elevation={0}
      sx={{
        p: 3,
        borderRadius: 3,
        bgcolor: allVideosViewed
          ? alpha(theme.palette.success.main, 0.04)
          : alpha(theme.palette.warning.main, 0.04),
        border: `1px solid ${
          allVideosViewed
            ? alpha(theme.palette.success.main, 0.2)
            : alpha(theme.palette.warning.main, 0.2)
        }`,
      }}
    >
      <Stack spacing={2.5} alignItems="center">
        <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 1 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            {allVideosViewed ? (
              <CheckCircleIcon sx={{ color: theme.palette.success.main, fontSize: 20 }} />
            ) : (
              <VideoLibraryIcon sx={{ color: theme.palette.warning.main, fontSize: 20 }} />
            )}
            <Typography
              variant="subtitle2"
              sx={{
                fontWeight: 700,
                color: allVideosViewed ? theme.palette.success.main : theme.palette.warning.main,
              }}
            >
              {allVideosViewed ? "All Videos Reviewed" : "Review Required"}
            </Typography>
          </Box>
          <Chip
            label={`${viewedCount}/${totalVideos} viewed`}
            size="small"
            sx={{
              bgcolor: allVideosViewed
                ? alpha(theme.palette.success.main, 0.15)
                : alpha(theme.palette.warning.main, 0.15),
              color: allVideosViewed ? theme.palette.success.dark : theme.palette.warning.dark,
              fontWeight: 600,
              fontSize: "0.75rem",
            }}
          />
        </Box>

        <Box sx={{ width: "100%", maxWidth: 400 }}>
          <Box
            sx={{
              height: 6,
              borderRadius: 3,
              bgcolor: alpha(theme.palette.grey[500], 0.15),
              overflow: "hidden",
            }}
          >
            <Box
              sx={{
                height: "100%",
                width: `${(viewedCount / totalVideos) * 100}%`,
                borderRadius: 3,
                bgcolor: allVideosViewed
                  ? theme.palette.success.main
                  : theme.palette.warning.main,
                transition: "width 0.3s ease-in-out",
              }}
            />
          </Box>
        </Box>

        <Box sx={{ display: "flex", justifyContent: "center", gap: 1, flexWrap: "wrap" }}>
          {Array.from({ length: totalVideos }).map((_, index) => {
            const videoNum = index + 1;
            const isViewed = viewedVideos.has(videoNum);
            const isCurrent = currentPage === videoNum;
            return (
              <Box
                key={videoNum}
                onClick={() => handlePageChange({} as React.ChangeEvent<unknown>, videoNum)}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  width: 36,
                  height: 36,
                  borderRadius: "50%",
                  cursor: "pointer",
                  transition: "all 0.2s ease-in-out",
                  border: isCurrent
                    ? `2px solid ${theme.palette.primary.main}`
                    : `2px solid ${
                        isViewed
                          ? theme.palette.success.main
                          : alpha(theme.palette.grey[400], 0.5)
                      }`,
                  bgcolor: isCurrent
                    ? theme.palette.primary.main
                    : isViewed
                      ? alpha(theme.palette.success.main, 0.1)
                      : "transparent",
                  color: isCurrent
                    ? "white"
                    : isViewed
                      ? theme.palette.success.main
                      : theme.palette.grey[500],
                  fontWeight: 600,
                  fontSize: "0.85rem",
                  "&:hover": {
                    transform: "scale(1.1)",
                    bgcolor: isCurrent
                      ? theme.palette.primary.main
                      : alpha(theme.palette.primary.main, 0.1),
                  },
                }}
              >
                {isViewed && !isCurrent ? <CheckCircleIcon sx={{ fontSize: 18 }} /> : videoNum}
              </Box>
            );
          })}
        </Box>

        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 2 }}>
          <Button
            variant="outlined"
            size="small"
            startIcon={<ArrowBackIcon />}
            onClick={handlePrevVideo}
            disabled={currentPage === 1}
            sx={{ borderRadius: 2, textTransform: "none", fontWeight: 600, minWidth: 100 }}
          >
            Previous
          </Button>
          <Typography variant="body2" sx={{ fontWeight: 600, color: theme.palette.text.secondary, px: 2 }}>
            Video {currentPage} of {totalVideos}
          </Typography>
          <Button
            variant={!viewedVideos.has(currentPage + 1) && currentPage < totalVideos ? "contained" : "outlined"}
            size="small"
            endIcon={<ArrowForwardIcon />}
            onClick={handleNextVideo}
            disabled={currentPage === totalVideos}
            sx={{
              borderRadius: 2,
              textTransform: "none",
              fontWeight: 600,
              minWidth: 100,
              ...(!viewedVideos.has(currentPage + 1) && currentPage < totalVideos && {
                animation: "pulse 2s infinite",
                "@keyframes pulse": {
                  "0%": { boxShadow: `0 0 0 0 ${alpha(theme.palette.primary.main, 0.4)}` },
                  "70%": { boxShadow: `0 0 0 8px ${alpha(theme.palette.primary.main, 0)}` },
                  "100%": { boxShadow: `0 0 0 0 ${alpha(theme.palette.primary.main, 0)}` },
                },
              }),
            }}
          >
            Next
          </Button>
        </Box>

        {!allVideosViewed && (
          <Typography
            variant="caption"
            sx={{
              textAlign: "center",
              color: theme.palette.warning.dark,
              bgcolor: alpha(theme.palette.warning.main, 0.1),
              px: 2,
              py: 1,
              borderRadius: 2,
              fontWeight: 500,
            }}
          >
            Please review all {totalVideos} videos before generating the report. Click through each video to mark it as viewed.
          </Typography>
        )}
      </Stack>
    </Paper>
  );
};
